import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import DesignCard from "../components/DesignCard";
import SignInPromptModal from "../components/SignInPromptModal";
import { assetUrl } from "../lib/format";
import {
  Heart,
  MessageSquare,
  ChevronRight,
  ArrowUpRight,
  ArrowLeft,
} from "lucide-react";
import BackBar from "../components/BackBar";

/* ───────── Reveal-on-scroll helper ───────── */

function useInView({ threshold = 0.15, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, as: As = "div", style, ...rest }) {
  const [ref, inView] = useInView();
  return (
    <As
      ref={ref}
      className={`wn-reveal${inView ? " is-visible" : ""}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </As>
  );
}

/* ───────── Page ───────── */

export default function ArchitectProfile() {
  const { architectId } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptAction, setPromptAction] = useState("");

  useEffect(() => {
    setA(null);
    setErr(null);
    api
      .get(`/architects/${architectId}`)
      .then((r) => setA(r.data))
      .catch((e) => setErr(e));
  }, [architectId]);

  const gated = (label) => () => {
    setPromptAction(label);
    setPromptOpen(true);
  };

  if (err) {
    return (
      <div data-testid="architect-error" style={{ color: "#A23B2E" }}>
        Could not load this architect.{" "}
        <Link to="/architects" style={{ color: "#1B1F4B" }}>
          Back to architects
        </Link>
      </div>
    );
  }
  if (!a) {
    return (
      <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
        Loading profile…
      </div>
    );
  }

  const firstName = (a.name || "").split(",")[0].split(" ")[0];
  const flags = a.demographic_flags || [];
  const paragraphs = (a.practice_statement || "").split(/\n{2,}/);
  const pullIdx = Math.max(1, Math.floor((paragraphs.length * 2) / 3));

  const highlights = a.project_highlights || [];
  const portfolio = a.portfolio_designs || [];
  const portfolioShown = portfolio.slice(0, 6);

  const leadArea = (a.service_areas && a.service_areas[0]) || "Indianapolis";

  return (
    <section
      data-testid="architect-profile-page"
      className="animate-fade-in-up"
    >
      <BackBar
        parent={{ to: "/architects", label: "Architects" }}
        trail={[
          { label: "Architects", to: "/architects" },
          { label: a.name },
        ]}
      />

      {/* ─── § 01 · Editorial hero ─── */}
      <Reveal>
        <header
          className="grid lg:grid-cols-[1fr_auto] gap-10 items-end"
          style={{ paddingBottom: 32 }}
          data-testid="arch-hero"
        >
          <div>
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 28,
                  height: 2,
                  background: "#B14E2F",
                }}
              />
              <span className="wn-label" style={{ color: "#8F3C23" }}>
                § 01 · Verified network architect
              </span>
            </div>

            <h1
              className="mt-5"
              data-testid="arch-name"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: "clamp(40px, 5.6vw, 64px)",
                fontWeight: 500,
                lineHeight: 1.04,
                letterSpacing: "-0.01em",
                color: "#1B1F4B",
                margin: 0,
              }}
            >
              {a.name}
            </h1>

            <div
              className="mt-3"
              data-testid="arch-firm"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: "clamp(20px, 2vw, 26px)",
                lineHeight: 1.3,
              }}
            >
              {a.firm}
            </div>

            {/* "X designs in the catalog" — quick-jump pills back to
                each design this firm has on the marketplace. Sits
                right under the firm name so a curious browser who
                arrived from a single design card can immediately
                discover the architect's broader catalog. */}
            {portfolio.length > 0 && (
              <div
                className="mt-4 flex flex-wrap items-center"
                data-testid="arch-catalog-pills"
                style={{ gap: 8 }}
              >
                <span
                  className="wn-label"
                  style={{ color: "#8F3C23", letterSpacing: "0.08em" }}
                >
                  {portfolio.length}{" "}
                  {portfolio.length === 1 ? "design" : "designs"} in the
                  catalog
                </span>
                <span
                  aria-hidden
                  style={{
                    color: "#D9D8E4",
                    fontSize: 14,
                  }}
                >
                  ·
                </span>
                {portfolio.map((d) => (
                  <Link
                    key={d.id}
                    to={`/marketplace/${d.id}`}
                    data-testid={`arch-catalog-pill-${d.id}`}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 999,
                      border: "1px solid #D9D8E4",
                      background: "#FBFAFD",
                      color: "#1B1F4B",
                      fontSize: 12,
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontWeight: 600,
                      letterSpacing: "0.01em",
                      textDecoration: "none",
                      transition: "all 160ms ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#B14E2F";
                      e.currentTarget.style.color = "#8F3C23";
                      e.currentTarget.style.background = "rgba(177,78,47,0.06)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#D9D8E4";
                      e.currentTarget.style.color = "#1B1F4B";
                      e.currentTarget.style.background = "#FBFAFD";
                    }}
                  >
                    {d.name}
                  </Link>
                ))}
              </div>
            )}

            {a.firm_tagline && (
              <p
                className="mt-5"
                data-testid="arch-tagline"
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  color: "#272636",
                  fontSize: 19,
                  lineHeight: 1.55,
                  maxWidth: 680,
                  margin: 0,
                }}
              >
                {a.firm_tagline}
              </p>
            )}

            {/* Track-record stat strip — the firm's headline numbers
                rendered as a tight 4-up row of stat blocks, separated by
                hairline dividers. Drives the "is this a serious firm
                with a track record?" first-glance question before the
                visitor scrolls. Values come from the same prior_builds
                source of truth as the per-design badges and the § 05
                track-record line, so the chain is internally consistent. */}
            {(() => {
              const yearsPractising = a.years_practising || 0;
              const designsInRegistry = portfolio.length;
              const homesBuilt = portfolio.reduce(
                (sum, d) => sum + (d.prior_builds || []).length,
                0
              );
              // "Returning contractors" — count of DISTINCT firms
              // (keyed by license_no) that have pulled permits on
              // more than one build of the SAME design across the
              // entire portfolio. A firm returning on Plan A AND on
              // Plan B counts once.
              const returningContractorKeys = new Set();
              portfolio.forEach((d) => {
                const perDesignCounts = {};
                (d.prior_builds || []).forEach((b) => {
                  const seen = new Set();
                  (b.contractors || []).forEach((c) => {
                    const k = (c.license_no || c.name || "").trim();
                    if (!k || seen.has(k)) return;
                    seen.add(k);
                    perDesignCounts[k] = (perDesignCounts[k] || 0) + 1;
                  });
                });
                Object.entries(perDesignCounts).forEach(([k, n]) => {
                  if (n > 1) returningContractorKeys.add(k);
                });
              });
              const returningContractors = returningContractorKeys.size;

              const stats = [];
              if (yearsPractising > 0) {
                stats.push({
                  id: "years",
                  value: yearsPractising,
                  label:
                    yearsPractising === 1
                      ? "year practising"
                      : "years practising",
                });
              }
              if (designsInRegistry > 0) {
                stats.push({
                  id: "designs",
                  value: designsInRegistry,
                  label:
                    designsInRegistry === 1
                      ? "design in registry"
                      : "designs in registry",
                });
              }
              if (homesBuilt > 0) {
                stats.push({
                  id: "homes",
                  value: homesBuilt,
                  label: homesBuilt === 1 ? "home built" : "homes built",
                  emphasised: true,
                });
              }
              if (returningContractors > 0) {
                stats.push({
                  id: "returning",
                  value: returningContractors,
                  label:
                    returningContractors === 1
                      ? "returning contractor"
                      : "returning contractors",
                  emphasised: true,
                });
              }
              if (stats.length === 0) return null;
              return (
                <div
                  className="mt-6"
                  data-testid="arch-track-record-strip"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "stretch",
                    borderTop: "1px solid #D9D8E4",
                    borderBottom: "1px solid #D9D8E4",
                    maxWidth: 680,
                  }}
                >
                  {stats.map((s, i) => {
                    const valColor = s.emphasised ? "#3A4A35" : "#1B1F4B";
                    return (
                      <div
                        key={s.id}
                        data-testid={`arch-track-stat-${s.id}`}
                        style={{
                          flex: "1 1 0",
                          minWidth: 130,
                          padding: "14px 18px",
                          borderLeft:
                            i === 0 ? "none" : "1px solid #D9D8E4",
                        }}
                      >
                        <div
                          className="tabular-nums"
                          style={{
                            fontFamily: '"Fraunces", Georgia, serif',
                            fontWeight: 500,
                            fontSize: 30,
                            color: valColor,
                            lineHeight: 1,
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {s.value}
                        </div>
                        <div
                          className="wn-label mt-2"
                          style={{
                            color: "#6C6B85",
                            letterSpacing: "0.08em",
                            lineHeight: 1.3,
                          }}
                        >
                          {s.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Right-rail dateline */}
          <aside
            className="hidden lg:block"
            style={{
              borderLeft: "1px solid #D9D8E4",
              paddingLeft: 20,
              minWidth: 200,
            }}
            data-testid="arch-dateline"
          >
            <div className="wn-label" style={{ color: "#6C6B85" }}>
              Volume 01 · Network
            </div>
            <div
              className="mt-1"
              style={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                color: "#1B1F4B",
                fontSize: 15,
                letterSpacing: "0.02em",
              }}
            >
              {a.firm}
            </div>
            <div
              className="mt-3"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {a.years_founded ? `Founded ${a.years_founded}` : "Indianapolis"}
              {a.years_founded && ` · ${leadArea}`}
            </div>
          </aside>
        </header>
      </Reveal>

      {/* ─── § 02 · Firm photo + At-a-glance card ─── */}
      <div
        className="grid lg:grid-cols-[1.05fr_1fr] gap-10 items-stretch"
        style={{ paddingTop: 24 }}
        data-testid="arch-photo-fact"
      >
        <Reveal style={{ display: "flex" }}>
          <div
            data-testid="arch-firm-photo"
            style={{
              width: "100%",
              minHeight: 460,
              border: "1px solid #D9D8E4",
              borderRadius: 4,
              overflow: "hidden",
              backgroundColor: "#EEEDF3",
              backgroundImage: a.firm_photo
                ? `url(${assetUrl(a.firm_photo)})`
                : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </Reveal>

        <Reveal delay={140}>
          <AtAGlanceCard architect={a} flags={flags} leadArea={leadArea} />
        </Reveal>
      </div>

      {/* ─── § 03 · How we work (editorial article) ─── */}
      <SectionDivider />
      <Reveal>
        <SectionHeading
          marker="§ 03"
          eyebrow="The work"
          title="How we practise."
          dek={`In ${firstName}'s own words — drawn from the firm's practice statement.`}
        />
      </Reveal>
      <Reveal delay={100}>
        <div
          className="mt-10 grid lg:grid-cols-[minmax(0,1fr)_300px] gap-12 items-start"
        >
          <div
            data-testid="practice-body"
            style={{
              color: "#272636",
              fontSize: 17,
              lineHeight: 1.72,
            }}
          >
            {paragraphs.map((p, i) => (
              <React.Fragment key={i}>
                <p style={{ marginTop: i === 0 ? 0 : 18 }}>{p}</p>
                {a.philosophy_quote && i === pullIdx - 1 && (
                  <blockquote
                    data-testid="practice-pullquote"
                    style={{
                      margin: "36px 0",
                      padding: "4px 0 4px 24px",
                      borderLeft: "3px solid #B14E2F",
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                      fontSize: 30,
                      lineHeight: 1.35,
                      color: "#1B1F4B",
                    }}
                  >
                    “{a.philosophy_quote}”
                  </blockquote>
                )}
              </React.Fragment>
            ))}
          </div>

          <PracticeByline architect={a} firstName={firstName} />
        </div>
      </Reveal>

      {/* ─── § 04 · What we do, who we serve ─── */}
      <SectionDivider />
      <Reveal>
        <SectionHeading
          marker="§ 04"
          eyebrow="What we do · who we serve"
          title="The brief, in plain terms."
        />
      </Reveal>
      <Reveal delay={100}>
        <div
          className="mt-10 grid md:grid-cols-2 gap-14"
          data-testid="focus-section"
        >
          <ChipBlock
            title="What we specialise in"
            items={
              a.specialisation_labels ||
              (a.specialisations || []).map((s) => s.replace(/_/g, " "))
            }
            testid="specialisations"
          />
          <ChipBlock
            title="Who we work for"
            items={a.lifestyle_focuses || []}
            testid="lifestyles"
          />
        </div>
      </Reveal>
      <Reveal delay={140}>
        <div
          className="mt-12"
          style={{
            borderTop: "1px solid #D9D8E4",
            borderBottom: "1px solid #D9D8E4",
            padding: "22px 0",
          }}
          data-testid="service-areas-strip"
        >
          <div className="grid lg:grid-cols-[200px_1fr] gap-10 items-center">
            <div className="wn-label" style={{ color: "#6C6B85" }}>
              Service areas
            </div>
            <div className="flex flex-wrap" style={{ gap: 10 }}>
              {(a.service_areas || []).map((s) => (
                <span
                  key={s}
                  data-testid={`service-area-${s}`}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 999,
                    border: "1px solid #D9D8E4",
                    backgroundColor: "transparent",
                    color: "#1B1F4B",
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      {/* ─── § 05 · Designs in the Registry ─── */}
      {portfolio.length > 0 && (
        <>
          <SectionDivider />
          {(() => {
            const builtCount = portfolio.reduce(
              (sum, d) => sum + ((d.prior_builds || []).length),
              0
            );
            const builtFromCount = portfolio.filter(
              (d) => (d.prior_builds || []).length > 0
            ).length;
            return (
              <Reveal>
                <SectionHeading
                  marker="§ 05"
                  eyebrow="Designs in the Registry"
                  title="Available now."
                  trailing={
                    <div
                      data-testid="portfolio-track-record"
                      style={{ textAlign: "right" }}
                    >
                      <div
                        className="wn-label"
                        style={{ color: "#6C6B85" }}
                      >
                        {portfolio.length}{" "}
                        {portfolio.length === 1 ? "design" : "designs"} from{" "}
                        {a.firm}
                      </div>
                      {builtCount > 0 && (
                        <div
                          className="mt-1"
                          style={{
                            fontFamily: '"Newsreader", Georgia, serif',
                            fontStyle: "italic",
                            color: "#4F6149",
                            fontSize: 13,
                            lineHeight: 1.4,
                          }}
                        >
                          <span
                            className="tabular-nums"
                            style={{
                              fontFamily: '"Nunito Sans", sans-serif',
                              fontStyle: "normal",
                              fontWeight: 700,
                              color: "#3A4A35",
                            }}
                          >
                            {builtCount}
                          </span>{" "}
                          completed{" "}
                          {builtCount === 1 ? "home" : "homes"} built from{" "}
                          {builtFromCount === portfolio.length
                            ? "these drawings"
                            : `${builtFromCount} of ${portfolio.length} ${
                                portfolio.length === 1 ? "drawing" : "drawings"
                              }`}
                        </div>
                      )}
                    </div>
                  }
                />
              </Reveal>
            );
          })()}
          <Reveal delay={100}>
            <div
              className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-[26px]"
              data-testid="portfolio-grid"
            >
              {portfolioShown.map((d) => (
                <DesignCard key={d.id} design={d} />
              ))}
            </div>
          </Reveal>
          {portfolio.length > portfolioShown.length && (
            <Reveal delay={140}>
              <Link
                to={`/marketplace?architect=${encodeURIComponent(a.id)}`}
                className="mt-8 inline-flex items-center wn-cta-arrow"
                style={{
                  color: "#8F3C23",
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: 13.5,
                  letterSpacing: "0.02em",
                  gap: 6,
                  textDecoration: "none",
                }}
                data-testid="portfolio-see-all"
              >
                See all {portfolio.length} designs by {a.firm}
                <ArrowUpRight size={14} strokeWidth={2.2} />
              </Link>
            </Reveal>
          )}
        </>
      )}

      {/* ─── § 06 · Completed work (case studies) ─── */}
      {highlights.length > 0 && (
        <>
          <SectionDivider />
          <Reveal>
            <SectionHeading
              marker="§ 06"
              eyebrow="Completed work"
              title="What it looks like, finished."
              dek="Each project below was built from the firm's drawings. Metrics are the owner's first-year measured numbers — not pre-design estimates."
            />
          </Reveal>
          <div className="mt-12">
            {highlights.map((h, i) => (
              <Reveal key={i} delay={i === 0 ? 0 : 100}>
                <CaseStudy h={h} imageRight={i % 2 === 1} index={i} />
              </Reveal>
            ))}
          </div>
        </>
      )}

      {/* ─── § 07 · Credentials & education ─── */}
      <SectionDivider />
      <Reveal>
        <SectionHeading
          marker="§ 07"
          eyebrow="The record"
          title="Credentials & education."
        />
      </Reveal>
      <Reveal delay={100}>
        <div
          className="mt-10 grid md:grid-cols-3 gap-10"
          style={{
            borderTop: "1px solid #D9D8E4",
            borderBottom: "1px solid #D9D8E4",
            padding: "32px 0",
          }}
          data-testid="credentials-section"
        >
          <Column title="Credentials">
            {(a.credentials || []).map((c, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <div
                  style={{
                    color: "#272636",
                    fontWeight: 700,
                    fontSize: 14,
                    letterSpacing: "0.02em",
                  }}
                >
                  {c.abbr || c.credential || "—"}
                  {c.year && (
                    <span
                      className="tabular-nums"
                      style={{
                        marginLeft: 8,
                        fontFamily: '"Newsreader", Georgia, serif',
                        fontStyle: "italic",
                        fontWeight: 400,
                        color: "#6C6B85",
                        fontSize: 13,
                      }}
                    >
                      {c.year}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    color: "#6C6B85",
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    marginTop: 2,
                  }}
                >
                  {c.name || c.description || ""}
                </div>
              </div>
            ))}
          </Column>
          <Column title="Education">
            {(a.education || []).map((e, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <div style={{ color: "#272636", fontWeight: 700, fontSize: 14 }}>
                  {e.degree || "—"}
                </div>
                <div
                  style={{ color: "#272636", fontSize: 13.5, marginTop: 2 }}
                >
                  {e.institution || ""}
                </div>
                {e.year && (
                  <div
                    className="tabular-nums"
                    style={{
                      color: "#6C6B85",
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                      fontSize: 13,
                      marginTop: 2,
                    }}
                  >
                    {e.year}
                  </div>
                )}
              </div>
            ))}
          </Column>
          <Column title="Recognition">
            {(a.awards || []).map((aw, i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <div
                  style={{
                    color: "#272636",
                    fontWeight: 600,
                    fontSize: 14,
                    lineHeight: 1.35,
                  }}
                >
                  {aw.name}
                  {aw.year && (
                    <span
                      className="tabular-nums"
                      style={{
                        color: "#6C6B85",
                        fontWeight: 500,
                        marginLeft: 6,
                      }}
                    >
                      · {aw.year}
                    </span>
                  )}
                </div>
                <div style={{ color: "#6C6B85", fontSize: 12, marginTop: 2 }}>
                  {aw.body}
                </div>
                {aw.description && (
                  <div
                    style={{
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                      color: "#272636",
                      fontSize: 13,
                      marginTop: 4,
                      lineHeight: 1.55,
                    }}
                  >
                    {aw.description}
                  </div>
                )}
              </div>
            ))}
          </Column>
        </div>
      </Reveal>

      {/* ─── § 08 · Inverted indigo action band ─── */}
      <Reveal delay={60}>
        <WorkWithBand
          architect={a}
          firstName={firstName}
          onConsult={gated(`Request a consultation with ${a.name}`)}
          onSave={gated("Save this architect")}
        />
      </Reveal>

      <Link
        to="/architects"
        className="mt-12 inline-flex items-center gap-1"
        style={{ color: "#6C6B85", fontSize: 13 }}
        data-testid="back-to-architects"
      >
        <ArrowLeft size={14} />
        Back to architects
      </Link>

      <SignInPromptModal
        open={promptOpen}
        onOpenChange={setPromptOpen}
        action={promptAction}
      />
    </section>
  );
}

/* ─────────── Practice byline (right of § 03 article) ─────────── */

function PracticeByline({ architect, firstName }) {
  const stats = [
    architect.years_founded
      ? { label: "Founded", value: architect.years_founded }
      : null,
    architect.team_size
      ? {
          label: "Team",
          value: `${architect.team_size}`,
        }
      : null,
    architect.years_practising
      ? { label: "Yrs practising", value: `${architect.years_practising}+` }
      : null,
  ].filter(Boolean);

  return (
    <aside
      data-testid="practice-byline"
      className="lg:sticky"
      style={{
        top: 32,
        border: "1px solid #D9D8E4",
        backgroundColor: "#FBFAFD",
        borderRadius: 4,
        padding: "22px 24px 22px",
      }}
    >
      <div className="wn-label" style={{ color: "#8F3C23" }}>
        Byline
      </div>

      <h3
        className="mt-2"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 22,
          lineHeight: 1.18,
          letterSpacing: "-0.005em",
          color: "#1B1F4B",
          margin: 0,
        }}
      >
        {architect.name}
      </h3>
      <div
        className="mt-1"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 15,
          lineHeight: 1.35,
        }}
      >
        {architect.firm}
      </div>

      {architect.bio && (
        <p
          className="mt-4"
          style={{
            color: "#272636",
            fontSize: 14,
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          {architect.bio}
        </p>
      )}

      {stats.length > 0 && (
        <div
          className="mt-5 grid"
          style={{
            gridTemplateColumns: `repeat(${stats.length}, minmax(0,1fr))`,
            columnGap: 14,
            borderTop: "1px solid #D9D8E4",
            paddingTop: 14,
          }}
        >
          {stats.map((s) => (
            <div key={s.label}>
              <div
                className="tabular-nums"
                style={{
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 800,
                  fontSize: 22,
                  color: "#1B1F4B",
                  lineHeight: 1,
                  letterSpacing: "-0.005em",
                }}
              >
                {s.value}
              </div>
              <div
                className="wn-label mt-1"
                style={{ color: "#6C6B85" }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        className="mt-5"
        style={{
          borderTop: "1px solid #D9D8E4",
          paddingTop: 14,
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 13,
          lineHeight: 1.55,
        }}
      >
        Written by {firstName}, edited for the public registry. The full
        practice statement is filed with the platform charter.
      </div>
    </aside>
  );
}

/* ─────────── At-a-glance card (right of firm photo) ─────────── */

function AtAGlanceCard({ architect, flags, leadArea }) {
  return (
    <div
      data-testid="arch-at-a-glance"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* §  At a glance */}
      <div style={{ padding: "22px 24px 22px" }}>
        <SectionLabel>At a glance</SectionLabel>
        <div
          className="grid grid-cols-2 mt-3"
          style={{ columnGap: 24, rowGap: 16 }}
        >
          <Spec
            label="Founded"
            value={architect.years_founded ?? "—"}
            numeric
          />
          <Spec
            label="Practitioners"
            value={architect.team_size ?? "—"}
            numeric
          />
          <Spec
            label="Years practising"
            value={
              architect.years_practising
                ? `${architect.years_practising}+`
                : "—"
            }
            numeric
          />
          <Spec label="Lead area" value={leadArea} />
        </div>
      </div>

      {/* §  Designations */}
      <div
        style={{
          padding: "20px 24px 22px",
          borderTop: "1px solid #D9D8E4",
        }}
      >
        <SectionLabel>Designations</SectionLabel>
        <div className="mt-3 flex flex-wrap" style={{ gap: 8 }}>
          {flags.length === 0 ? (
            <span
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 14,
              }}
            >
              No business designations on file.
            </span>
          ) : (
            flags.map((f) => (
              <span
                key={f}
                data-testid={`arch-flag-${f}`}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid #D9D8E4",
                  backgroundColor: "transparent",
                  color: "#1B1F4B",
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: 10.5,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                }}
              >
                {f}
              </span>
            ))
          )}
        </div>
        {architect.license_no && (
          <div
            className="mt-4 tabular-nums"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              color: "#6C6B85",
              fontSize: 12,
              letterSpacing: "0.04em",
            }}
          >
            Indiana licence&nbsp;·&nbsp;
            <span style={{ color: "#1B1F4B", fontWeight: 700 }}>
              {architect.license_no}
            </span>
          </div>
        )}
      </div>

      {/* §  Platform charter pledge — flex:1 so the card matches photo height */}
      <div
        style={{
          padding: "20px 24px 22px",
          borderTop: "1px solid #D9D8E4",
          flex: 1,
        }}
      >
        <SectionLabel>Platform charter</SectionLabel>
        <p
          className="mt-3"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#272636",
            fontSize: 15,
            lineHeight: 1.55,
            margin: 0,
          }}
        >
          Has signed the WHOLE&nbsp;Neighborhoods architect charter — carries
          current E&amp;O insurance, accepts paid consultations through the
          platform, and publishes pre-reviewed designs to a public registry.
        </p>
      </div>
    </div>
  );
}

/* ─────────── Case study (editorial, hairline only) ─────────── */

function CaseStudy({ h, imageRight, index }) {
  const photo = h.image_ref ? assetUrl(h.image_ref) : null;
  const designName = h.built_from_design_name || h.built_from_design_id;
  return (
    <article
      data-testid={`case-study-${index}`}
      className="grid md:grid-cols-2 gap-10"
      style={{ marginBottom: 72 }}
    >
      <div style={{ order: imageRight ? 2 : 1 }}>
        <div
          style={{
            aspectRatio: "16 / 11",
            backgroundColor: "#EEEDF3",
            backgroundImage: photo ? `url(${photo})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            border: "1px solid #D9D8E4",
            borderRadius: 4,
          }}
        />
      </div>
      <div style={{ order: imageRight ? 1 : 2 }}>
        {designName && h.built_from_design_id && (
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 22,
                height: 2,
                background: "#B14E2F",
              }}
            />
            <Link
              to={`/marketplace/${h.built_from_design_id}`}
              className="wn-label"
              style={{ color: "#8F3C23" }}
              data-testid="case-built-from"
            >
              Built from {designName}
              {h.completion_year && (
                <span className="tabular-nums">
                  {" · "}
                  {h.completion_year}
                </span>
              )}
            </Link>
          </div>
        )}
        <h3
          className="mt-4"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: "clamp(24px, 2.3vw, 30px)",
            color: "#1B1F4B",
            lineHeight: 1.12,
            letterSpacing: "-0.005em",
            margin: 0,
          }}
        >
          {h.project_name}
        </h3>
        {(h.neighborhood || h.lot_dimensions) && (
          <div
            className="mt-2"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 15,
            }}
          >
            {[h.neighborhood, h.lot_dimensions].filter(Boolean).join(" · ")}
          </div>
        )}
        {h.narrative && (
          <p
            style={{
              color: "#272636",
              fontSize: 15,
              lineHeight: 1.7,
              marginTop: 16,
            }}
          >
            {h.narrative}
          </p>
        )}
        {Array.isArray(h.metrics) && h.metrics.length > 0 && (
          <div
            className="mt-6 grid grid-cols-2"
            style={{
              gap: 18,
              borderTop: "1px solid #D9D8E4",
              paddingTop: 16,
            }}
          >
            {h.metrics.map((m, i) => (
              <div key={i}>
                <div className="wn-label" style={{ color: "#6C6B85" }}>
                  {m.label}
                </div>
                <div
                  className="tabular-nums"
                  style={{
                    fontFamily: '"Nunito Sans", sans-serif',
                    color: "#1B1F4B",
                    fontWeight: 700,
                    fontSize: 18,
                    marginTop: 4,
                    lineHeight: 1.2,
                  }}
                >
                  {m.value}
                </div>
              </div>
            ))}
          </div>
        )}
        {h.client_type && (
          <div
            style={{
              color: "#6C6B85",
              fontSize: 13,
              marginTop: 14,
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
            }}
          >
            Client:{" "}
            <span style={{ color: "#272636", fontWeight: 600, fontStyle: "normal" }}>
              {h.client_type}
            </span>
          </div>
        )}
      </div>
    </article>
  );
}

/* ─────────── § 08 · Inverted indigo action band ─────────── */

function WorkWithBand({ architect, firstName, onConsult, onSave }) {
  return (
    <div
      data-testid="work-with-band"
      style={{
        marginTop: 72,
        backgroundColor: "#1B1F4B",
        color: "#FBFAFD",
        borderRadius: 4,
        padding: "44px 44px 40px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: "#E8927C",
          }}
        />
        <span className="wn-label" style={{ color: "#E8927C" }}>
          § 08 · Work with {architect.firm}
        </span>
      </div>

      <div className="mt-5 grid lg:grid-cols-[1fr_auto] items-end gap-8">
        <div>
          <h3
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: "clamp(28px, 3.2vw, 38px)",
              lineHeight: 1.12,
              letterSpacing: "-0.005em",
              color: "#FBFAFD",
              margin: 0,
              maxWidth: 720,
            }}
          >
            Start a conversation with {firstName}.
          </h3>
          <p
            className="mt-3"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "rgba(251,250,253,0.72)",
              fontSize: 17,
              lineHeight: 1.55,
              maxWidth: 620,
              margin: 0,
            }}
          >
            All consultations are arranged through the platform — a structured
            brief, no inbox spam. A 45-minute paid session ($275) confirms fit,
            scope, and timeline. Refundable if the architect cannot proceed.
          </p>
        </div>

        <div className="flex items-center flex-wrap gap-3">
          <button
            type="button"
            onClick={onSave}
            data-testid="save-arch-btn"
            className="wn-cta-arrow"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 18px",
              borderRadius: 4,
              background: "transparent",
              color: "#FBFAFD",
              border: "1px solid rgba(251,250,253,0.45)",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "0.01em",
              cursor: "pointer",
              transition:
                "background-color 200ms ease-out, border-color 200ms ease-out",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(251,250,253,0.08)";
              e.currentTarget.style.borderColor = "#FBFAFD";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.borderColor =
                "rgba(251,250,253,0.45)";
            }}
          >
            <Heart size={15} />
            Save architect
          </button>
          <button
            type="button"
            onClick={onConsult}
            data-testid="consult-arch-btn"
            className="wn-cta-arrow"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 20px",
              borderRadius: 4,
              background: "#B14E2F",
              color: "#FBFAFD",
              border: "1px solid #B14E2F",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "0.01em",
              cursor: "pointer",
              transition:
                "background-color 200ms ease-out, border-color 200ms ease-out",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#8F3C23";
              e.currentTarget.style.borderColor = "#8F3C23";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#B14E2F";
              e.currentTarget.style.borderColor = "#B14E2F";
            }}
          >
            <MessageSquare size={15} />
            Request a consultation
            <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Shared editorial helpers ─────────── */

function SectionDivider() {
  return (
    <div
      aria-hidden
      style={{
        borderTop: "1px solid #D9D8E4",
        marginTop: 64,
        marginBottom: 36,
      }}
    />
  );
}

function SectionHeading({ marker, eyebrow, title, dek, trailing }) {
  return (
    <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-end">
      <div>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 28,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            {marker} · {eyebrow}
          </span>
        </div>
        {title && (
          <h2
            className="mt-4"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: "clamp(28px, 3.4vw, 38px)",
              lineHeight: 1.15,
              color: "#1B1F4B",
              letterSpacing: "-0.005em",
              margin: 0,
              paddingBottom: 4,
            }}
          >
            {title}
          </h2>
        )}
        {dek && (
          <p
            className="mt-3"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 16,
              lineHeight: 1.55,
              maxWidth: 760,
              margin: 0,
            }}
          >
            {dek}
          </p>
        )}
      </div>
      {trailing && <div>{trailing}</div>}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="wn-label" style={{ color: "#8F3C23" }}>
      {children}
    </div>
  );
}

function Spec({ label, value, numeric = false }) {
  return (
    <div>
      <div className="wn-label">{label}</div>
      <div
        className={numeric ? "tabular-nums" : ""}
        style={{
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: numeric ? 700 : 600,
          color: "#1B1F4B",
          fontSize: numeric ? 22 : 16,
          marginTop: 4,
          lineHeight: 1.15,
        }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function ChipBlock({ title, items, testid }) {
  return (
    <div data-testid={`chipblock-${testid}`}>
      <h3
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 22,
          color: "#1B1F4B",
          lineHeight: 1.2,
          margin: 0,
        }}
      >
        {title}
      </h3>
      <div className="mt-4 flex flex-wrap" style={{ gap: 8 }}>
        {items.length === 0 ? (
          <span
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 14,
            }}
          >
            —
          </span>
        ) : (
          items.map((it) => (
            <span
              key={it}
              style={{
                padding: "6px 13px",
                borderRadius: 999,
                border: "1px solid #D9D8E4",
                backgroundColor: "transparent",
                color: "#272636",
                fontFamily: '"Nunito Sans", sans-serif',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {it}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

function Column({ title, children }) {
  return (
    <div>
      <div className="wn-label" style={{ color: "#8F3C23" }}>
        {title}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}
